import { useEditor } from "@bigbluebutton/tldraw";
import React, { useEffect, useState } from "react";
import { compressImage, handleFiledrop, handleSavePresentation } from "../service";
import { PRESENTATIONS_SUBSCRIPTION } from "../queries";
import useDeduplicatedSubscription from "/imports/ui/core/hooks/useDeduplicatedSubscription";
import { useIsPresentationEnabled } from "/imports/ui/services/features";
import { notify } from "/imports/ui/services/notification";

export const DropEvents = ({
  setPresentation,
  toUploadCount,
  setToUploadCount,
  handleCurrentChange,
  intl,
  removePresentation,
  captureCurrentSlide,
  curPageId,
  isPrepareLesson
}) => {

  const editor = useEditor()
  const { data: presentationData } = useDeduplicatedSubscription(PRESENTATIONS_SUBSCRIPTION);

  const [presentations, setPresentations] = useState(presentationData?.pres_presentation || [])


  const isPresentationEnabled = useIsPresentationEnabled()

  const PRESENTATION_CONFIG = window.meetingClientSettings.public.presentation;

  const updateFileKey = (id, key, value, operation = '$set') => {
    setPresentations((prevPresentations) => {
      const fileIndex = prevPresentations.findIndex((f) => f.presentationId === id)
      if (fileIndex === -1) return prevPresentations
      const updated = update(prevPresentations, {
        [fileIndex]: {
          $apply: (file) =>
            update(file, {
              [key]: {
                [operation]: value,
              },
            }),
        },
      })
      return updated
    })

  }

  const deepMergeUpdateFileKey = (id, key, value) => {
    const applyValue = (toUpdate) => update(toUpdate, { $merge: value })
    updateFileKey(id, key, applyValue, '$apply')
  }

  useEffect(() => {
    if (!editor) return

    const enableDrag = (e) => {
      e.preventDefault()
      e.stopPropagation();
    }
    const onDrop = async (e) => {
      e.preventDefault()
      e.stopPropagation();
      const files = Array.from(e?.dataTransfer?.files || [])
      if (!files || files.length === 0) return
      for (const file of files) {
        const fileValidMimeTypes = window.meetingClientSettings.public.presentation.uploadValidMimeTypes;
        const validMimes = Array.isArray(fileValidMimeTypes)
          ? fileValidMimeTypes
            .map((fileValid) => fileValid?.mime)
            .filter((mime) => !mime?.startsWith('image/')) // exclude images
          : [];

        const type = file?.type;

        if (validMimes.includes(type))
        // (type === 'application/pdf' ||
        // type === 'application/vnd.ms-powerpoint' || // .ppt
        // type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || // .pptx
        // type === 'application/msword' || // .doc
        // type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') // .docx
        {
          if (!isPrepareLesson) {
            captureCurrentSlide()
          }
          const updatedPres = await handleFiledrop(file, files,
            presentations,
            setPresentations,
            toUploadCount,
            setToUploadCount,
            handleCurrentChange,
            intl,
            deepMergeUpdateFileKey,
            updateFileKey)
          const presentationsToSave = updatedPres;
          const propPresentations = updatedPres;
          return handleSavePresentation(
            presentationsToSave,
            true,
            {},
            propPresentations,
            setPresentation,
            removePresentation,
            isPresentationEnabled
          ).catch((error) => {
            logger.error({
              logCode: 'presentationuploader_component_save_error',
              extraInfo: { error },
            }, 'Presentation uploader catch error on confirm');
          });
          // alert(`PDF dropped: ${file.name}`)
        } else if (type.startsWith('image/')) {

          if (!file) return;

          const reader = new FileReader();
          reader.onload = async () => {
            const src = reader.result;
            const assetId = `asset:${Date.now()}`;
            const img = new Image();

            img.onload = async () => {
              const { width, height } = img;
              const maxSize = 300;
              const scaleUpFactor = 2; // ⬅️ make image 20% larger after resize

              let w = width;
              let h = height;

              if (w > h && w > maxSize) {
                const scale = maxSize / w;
                w = Math.round(maxSize * scaleUpFactor);
                h = Math.round(h * scale * scaleUpFactor);
              } else if (h >= w && h > maxSize) {
                const scale = maxSize / h;
                h = Math.round(maxSize * scaleUpFactor);
                w = Math.round(w * scale * scaleUpFactor);
              }
              // Create a smaller version (optional)
              // const w = width
              // const h = height


              const { compressedSrc, mimeType } = await compressImage(src, file.type, file.size);

              // Capture the event coordinates before the timeout
              const dropClientX = e.clientX;
              const dropClientY = e.clientY;
              const assetId = `asset:${Date.now()}`

              // If you saved drop position in meta, use it
              const bounds = editor.getContainer().getBoundingClientRect()
              const dropX = dropClientX - bounds.left
              const dropY = dropClientY - bounds.top
              const point = editor.screenToPage({ x: dropX, y: dropY })

              // Create the asset
              await editor.createAssets([
                {
                  id: assetId,
                  typeName: 'asset',
                  type: 'image',
                  props: {
                    src: compressedSrc,
                    w: w,
                    h: h,
                    name: file.name,
                    mimeType: file.type,
                    isAnimated: false,
                  },
                  meta: { x: point.x, y: point.y },
                },
              ]);


              if (editor.store.has(assetId)) {
                editor.createShape({
                  id: `shape:${Date.now()}`,
                  type: 'image',
                  x: point.x,
                  y: point.y,
                  props: {
                    assetId,
                    w,
                    h
                  },
                });

                // Switch back to select tool
                editor.setCurrentTool('select');
              }
              //   // Use a small delay to ensure the asset is loaded before creating the shape
              // setTimeout(() => {
              // }, 500); // 100ms delay
            };

            img.src = src;
          };

          reader.readAsDataURL(file);
          // Let Tldraw handle images normally
        } else {
          notify("File not supported....")
          // console.warn('Unsupported file type:', file?.type)
        }
      }
    }
    editor.getContainer().addEventListener('dragover', enableDrag)
    editor.getContainer().addEventListener('drop', onDrop)
    return () => {
      editor.getContainer().removeEventListener('drop', onDrop)
      editor.getContainer().removeEventListener('dragover', enableDrag)
    }
  }, [editor, PRESENTATION_CONFIG?.uploadValidMimeTypes])
  return null;
};
